import { z, ZodError } from "zod";
import type { ServerActionError, ServerActionResponse } from "../types";

function convertZodError(error: ZodError): ServerActionError {
  const { fieldErrors, formErrors } = z.flattenError(error);
  const [field] = Object.keys(fieldErrors) as (keyof typeof fieldErrors)[];
  const message = (field && fieldErrors[field]?.[0]) ?? formErrors[0] ?? "Validation failed";
  return {
    message,
    code: "VALIDATION_ERROR",
    field,
    fields: fieldErrors,
  };
}

function convertGenericError(error: unknown): ServerActionError {
  const message = error instanceof Error ? error.message : "An unexpected error occurred";

  return {
    message: process.env.NODE_ENV === "production" ? "An unexpected error occurred" : message,
    code: "INTERNAL_ERROR",
    statusCode: 500,
  };
}

export function handleServerActionError(
  error: unknown,
  customHandler?: (error: unknown) => ServerActionError | undefined
): { success: false; error: ServerActionError } {
  // Debug logging only in development
  if (process.env.NODE_ENV === 'development') {
    console.error("[Server Action Error]", error?.constructor?.name || 'Unknown', error instanceof Error ? error.message : error);
  }

  // Try custom handler first
  if (customHandler) {
    try {
      const customError = customHandler(error);
      if (customError) {
        return {
          success: false,
          error: customError,
        };
      }
    } catch (handlerError) {
      console.error("[Server Action Error] Custom handler threw an error:", handlerError);
    }
  }

  if (error instanceof ZodError) {
    return {
      success: false,
      error: convertZodError(error),
    };
  }

  if (error instanceof Error && "toServerActionError" in error && typeof error.toServerActionError === "function") {
    const serverError = error.toServerActionError() as ServerActionError;
    
    // Check if this is an auth error based on the error type/code
    if (serverError.code === "AUTHENTICATION_ERROR" || (error as any).type === "AUTHENTICATION_ERROR") {
      serverError.shouldRedirect = true;
      serverError.redirectTo = "/login";
    }
    
    return {
      success: false,
      error: serverError,
    };
  }

  return {
    success: false,
    error: convertGenericError(error),
  };
}

export function isErrorResponse<T>(response: ServerActionResponse<T>): response is { success: false; error: ServerActionError } {
  return !response.success;
}